import type { FC } from 'react';
import { Fragment, useState } from 'react';

import type { ModalComponent } from '../../types';

import { Button, Markdown } from '@snowit/ui';

import useBreakpoints from '../../../useBreakpoints';
import Modal from '../Modal/Modal';

import s from './ModalConfirm.module.scss';

const ModalConfirm: FC<ModalComponent> = (props: ModalComponent) => {
  const { id, body, onClose, confirm, size = 'df' } = props;
  const { minWidth } = useBreakpoints();
  const [loading, setLoading] = useState<boolean>(false);

  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose(id);
    }
  };

  const handleCancel = () => {
    handleClose();
    if (typeof confirm?.onCancel === 'function') {
      confirm.onCancel();
    }
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      if (typeof confirm?.onConfirm === 'function') {
        await confirm.onConfirm();
      }
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const renderBodyContent = () => {
    const bodyContent = typeof body === 'function' ? body() : body || '';

    if (typeof bodyContent === 'string') {
      return <Markdown source={bodyContent} />;
    }

    return bodyContent;
  };

  const renderFooter = () => {
    if (!confirm) return null;
    return (
      <Fragment>
        <Button
          classNames={{ button: s.confirmBtn }}
          label={confirm.confirmLabel}
          theme={'fill'}
          bgColor={'primary'}
          onClick={handleConfirm}
          loading={loading}
          test={'confirm_modal_confirm_button'}
        />
        <Button
          classNames={{ button: s.cancelBtn }}
          label={confirm.cancelLabel}
          theme={minWidth.sm ? 'text' : 'outline'}
          bgColor={'text'}
          onClick={handleCancel}
          test={'confirm_modal_cancel_button'}
        />
      </Fragment>
    );
  };

  return (
    <Modal
      {...props}
      onClose={handleCancel}
      classNames={{
        ...props.classNames,
        footer: s.footer,
      }}
      body={renderBodyContent}
      footer={renderFooter}
      theme={'confirm'}
      size={size}
    />
  );
};

export default ModalConfirm;
